/* import __COLOCATED_TEMPLATE__ from './projects-freelancer-calculate-potential-earnings.hbs'; */
import clsx from 'clsx';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask, timeout } from 'ember-concurrency';

const CLASS_NAME = 'projects-freelancer-calculate-potential-earnings';
const SUBTEXT_CLASS_NAME = 'calculate-potential-earnings-subtext';
const SUBTEXT_HAS_HEADER_CLASS_NAME = `${SUBTEXT_CLASS_NAME}--hasHeader`;
const DEBOUNCE_MS = 250;

const moneyFormatterUSD = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * @param {string} value
 * @returns {number}
 */
const parseMoneyUSD = (value) => {
  const formattedNumber = value.replace(/[^0-9.]/g, '');
  const parsedNumber = Number.parseFloat(formattedNumber);
  return Number.isFinite(parsedNumber) ? parsedNumber : 0;
};

/**
 * @param {number} value
 * @returns {string}
 */
const formatMoneyUSD = (value) => {
  return moneyFormatterUSD.format(value);
};

class ProjectsFreelancerCalculatePotentialEarnings extends Component {
  @service('commands/company-commands') companyCommandService;
  @service('error-tracker') errorTracker;
  @service store;

  @tracked earnings = formatMoneyUSD(0);
  @tracked offer = formatMoneyUSD(0);
  @tracked serviceFee = 0;
  @tracked selectFee = 0;

  @tracked isCalculatorExpanded = false;
  @tracked companySellerConnection = null;

  /**
   * @returns {string}
   */
  get classNames() {
    return clsx(this.args.class, CLASS_NAME);
  }

  get subTextClasses() {
    return clsx(
      SUBTEXT_CLASS_NAME,
      this.args.hasHeader && SUBTEXT_HAS_HEADER_CLASS_NAME,
    );
  }

  /**
   * @returns {boolean}
   */
  get showCalculator() {
    return this.isCalculatorExpanded || !this.args.isToggleView;
  }

  /**
   * @returns {number}
   */
  get #offerNum() {
    return parseMoneyUSD(this.offer);
  }

  /**
   * @returns {number}
   */
  get #earningsNum() {
    return parseMoneyUSD(this.earnings);
  }

  /**
   * @returns {number}
   */
  get #lifetimeRevenue() {
    return this.companySellerConnection
      ? (this.companySellerConnection.lifetimeEarningsInCents / 100).toFixed(2)
      : 0;
  }

  /**
   * @returns {boolean}
   */
  get #isInNetwork() {
    return this.companySellerConnection?.invited ?? false;
  }

  /**
   * @returns {boolean}
   */
  get #isSellerFeesCovered() {
    return Boolean(this.args.sellerFeesCovered);
  }

  get baseFee() {
    return this.companySellerConnection?.fees['base_fee'] ?? 0.0;
  }

  get feePercent() {
    return this.baseFee * 100;
  }

  get selectPercent() {
    const selectFee = this.companySellerConnection?.selectFee;
    return selectFee ? selectFee * 100 : '?';
  }

  /**
   * @returns {number}
   */
  get #selectFeePercent() {
    return this.args.isSelect
      ? this.companySellerConnection?.selectFee ?? 0
      : 0;
  }

  @action
  handleFocus(e) {
    e.target.select();
  }

  @action
  updateOfferField(e) {
    this.offer = e.target.value;
    this.throttledUpdateMoneyCalculationFromOffer.perform();
  }

  @action
  updateEarningsField(e) {
    this.earnings = e.target.value;
    this.throttledUpdateMoneyCalculationFromEarnings.perform();
  }

  @action
  blurOfferField() {
    this.offer = formatMoneyUSD(this.#offerNum);

    if (this.throttledUpdateMoneyCalculationFromOffer.isRunning) {
      this.throttledUpdateMoneyCalculationFromOffer.cancelAll();
      this.#updateMoneyCalculationFromOffer();
    }
  }

  @action
  blurEarningsField() {
    this.earnings = formatMoneyUSD(this.#earningsNum);

    if (this.throttledUpdateMoneyCalculationFromEarnings.isRunning) {
      this.throttledUpdateMoneyCalculationFromEarnings.cancelAll();
      this.#updateMoneyCalculationFromEarnings();
    }
  }

  @action
  onTogglePotentialEarningsCalculatorClicked(e) {
    e.preventDefault();

    // reset calculator on collapse
    if (this.isCalculatorExpanded) {
      this.earnings = formatMoneyUSD(0);
      this.offer = formatMoneyUSD(0);
      this.serviceFee = 0;
      this.selectFee = 0;
    }

    this.isCalculatorExpanded = !this.isCalculatorExpanded;
  }

  /**
   *
   */
  #updateMoneyCalculationFromOffer() {
    const offerNum = this.#offerNum;

    const serviceFee = offerNum * this.baseFee;
    const selectFee = offerNum * this.#selectFeePercent;
    const earnings = offerNum - serviceFee - selectFee;

    this.serviceFee = serviceFee
    this.selectFee = selectFee
    this.earnings = formatMoneyUSD(earnings);
  }

  /**
   *
   */
  #updateMoneyCalculationFromEarnings() {
    const serviceFee = requiredOffer * this.baseFee;
    const selectFee = requiredOffer * this.#selectFeePercent;

    this.serviceFee = serviceFee;
    this.selectFee = selectFee;
  }

  /**
   *
   */
  @restartableTask
  *throttledUpdateMoneyCalculationFromOffer() {
    yield timeout(DEBOUNCE_MS);
    this.#updateMoneyCalculationFromOffer();
  }

  /**
   *
   */
  @restartableTask
  *throttledUpdateMoneyCalculationFromEarnings() {
    yield timeout(DEBOUNCE_MS);
    this.#updateMoneyCalculationFromEarnings();
  }

  /**
   * Loads the relationship between a company and user
   *
   * @param {Company} company
   * @param {User} seller
   */
  @restartableTask
  *getCompanySellerConnection(company, seller) {
    try {
      const response = yield this.companyCommandService.loadCompanySellerConnection(company.id, seller.id);
      this.store.pushPayload(response);
      const companySellerConnection = this.store.peekRecord('companySellerConnection', response.data.id);
      this.companySellerConnection = companySellerConnection;

      // update calculations with new fees
      this.throttledUpdateMoneyCalculationFromOffer.perform();
    } catch (e) {
      this.errorTracker.error(`error getting CompanySellerConnection between company [${company.id}] and user [${seller.id}]`);
      throw e;
    }
  }
}

export default ProjectsFreelancerCalculatePotentialEarnings;
